import React, { FocusEventHandler, FunctionComponent, useCallback, useState } from 'react';
import { useIsFocused } from '../../hooks/useIsFocused';
import { useRefWithCurrentValue } from '../../hooks/useRefWithCurrentValue';

/**
 * Instead of calling onInput for every keypress, onInput get's called when the input is blurred.
 * This is great for performance if you e.g. hook up an entity to an input and don't want to send a network request for each keypress.
 */
export function withBufferedValue<TProps extends {
  value: any;
  onInput: (value: any) => void;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
}>(WrappedComponent: FunctionComponent<TProps>): (props: TProps) => React.JSX.Element {
  return props => {
    const {
      value,
      onInput,
      onBlur,
      onFocus
    } = props;
    const valueRef = useRefWithCurrentValue(value);
    const [internalValue, setInternalValue] = useState(value);
    const internalValueRef = useRefWithCurrentValue(internalValue);
    const {
      isFocused,
      handleBlur: isFocusedHandleBlur,
      handleFocus: isFocusedHandleFocus
    } = useIsFocused({
      onBlur,
      onFocus
    });
    const isFocusedRef = useRefWithCurrentValue(isFocused);
    const handleFocus: FocusEventHandler = useCallback(event => {
      setInternalValue(valueRef.current);
      isFocusedHandleFocus(event);
    }, [isFocusedHandleFocus, valueRef]);
    const handleBlur: FocusEventHandler = useCallback(event => {
      isFocusedHandleBlur(event);
      if (internalValueRef.current !== valueRef.current) {
        onInput(internalValueRef.current);
      }
    }, [isFocusedHandleBlur, internalValueRef, valueRef, onInput]);
    const handleInput = useCallback((inputValue: any) => {
      if (isFocusedRef.current) {
        setInternalValue(inputValue);
      } else {
        onInput(inputValue);
      }
    }, [setInternalValue, isFocusedRef, onInput]);
    return <WrappedComponent {...props} value={isFocused ? internalValue : value} onInput={handleInput} onBlur={handleBlur} onFocus={handleFocus}></WrappedComponent>;
  };
}